import React, { useState } from "react";

import { useGetAllFeatureFlags, useUpdateFeatureFlag } from "utils";

import { FEATURE_FLAG_NAMES, SUPPORTED_MARKETS } from "consts";
import type { SupportedMarket } from "consts";

import type { FeatureFlagsResponse } from "models";

import { AdminHead, Flex, Link, MainPage, Modal, Option, Select, Switch, TooltipWrapper, Typography, Message as message } from "components";

const COUNTRY_OPTIONS = Object.entries(SUPPORTED_MARKETS).map(([, code]) => ({ code }));

const getEnabledCountryCodes = (flag: FeatureFlagsResponse): SupportedMarket[] => flag.enabledCountryCodes ?? [];

const getFlagDisplayName = (flag: FeatureFlagsResponse) => FEATURE_FLAG_NAMES[flag.slug as keyof typeof FEATURE_FLAG_NAMES] || flag.slug;

export const FeatureFlags = () => {
  const { data: featureFlags, isLoading } = useGetAllFeatureFlags();
  const { mutateAsync: updateFeatureFlag, isPending } = useUpdateFeatureFlag();
  const [pendingGlobalFlag, setPendingGlobalFlag] = useState<FeatureFlagsResponse | null>(null);
  const [pendingCountryUpdate, setPendingCountryUpdate] = useState<{
    flag: FeatureFlagsResponse;
    enabledCountryCodes: SupportedMarket[];
  } | null>(null);

  const handleUpdateGlobal = async (featureFlagId: string, enabledGlobally: boolean) => {
    try {
      await updateFeatureFlag({
        featureFlagId,
        enabledGlobally,
      });
      message({
        icon: "tick",
        content: "Feature flag updated successfully",
      });
    } catch (error) {
      message({
        icon: "error",
        content: `Failed to update feature flag: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setPendingGlobalFlag(null);
    }
  };

  const handleUpdateCountries = async (flag: FeatureFlagsResponse, enabledCountryCodes: SupportedMarket[]) => {
    try {
      await updateFeatureFlag({
        featureFlagId: flag.id,
        enabledGlobally: flag.enabledGlobally,
        enabledCountryCodes,
      });
      message({
        icon: "tick",
        content: "Feature flag updated successfully",
      });
    } catch (error) {
      message({
        icon: "error",
        content: `Failed to update feature flag: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    } finally {
      setPendingCountryUpdate(null);
    }
  };

  const onConfirmGlobal = () => {
    if (pendingGlobalFlag) {
      handleUpdateGlobal(pendingGlobalFlag.id, true);
    }
  };

  const onConfirmCountry = () => {
    if (pendingCountryUpdate) {
      handleUpdateCountries(pendingCountryUpdate.flag, pendingCountryUpdate.enabledCountryCodes);
    }
  };

  return (
    <MainPage loading={isLoading || isPending} pageName={"admin"}>
      <AdminHead
        headline="Feature Flags"
        description={
          <Flex vertical gap="0.25rem">
            <Typography variant="paragraph-m" color="grey-600">
              Enable features globally or for specific countries. Global overrides country selection.
            </Typography>
            <Typography variant="paragraph-m" color="grey-600">
              Feature flags can also be enabled per carbon program. To manage program-level feature flags, go to{" "}
              <Link to="/carbon-programs/overview" className="primary-link">
                Carbon Programs Overview
              </Link>
              .
            </Typography>
          </Flex>
        }
      />
      {featureFlags && featureFlags.length > 0 ? (
        featureFlags.map((flag) => (
          <Flex className="margin-bottom-200" key={flag.id} align="center" gap="2rem">
            <Typography variant="paragraph-m">{FEATURE_FLAG_NAMES[flag.slug as keyof typeof FEATURE_FLAG_NAMES] || flag.slug}</Typography>
            <Flex align="center" gap="0.5rem" style={{ minWidth: "220px" }}>
              <Typography variant="paragraph-m" style={{ fontSize: "0.875rem" }} color="grey-600">
                {flag.enabledGlobally ? "Enabled globally" : "Disabled globally"}
              </Typography>
              <Switch
                checked={flag.enabledGlobally}
                onChange={(enabled) => {
                  if (enabled) {
                    setPendingGlobalFlag(flag);
                  } else {
                    handleUpdateGlobal(flag.id, false);
                  }
                }}
                disabled={isPending}
              />
            </Flex>
            <TooltipWrapper
              tooltip={{
                title: flag.enabledGlobally
                  ? "This feature is enabled globally and therefore available in all countries"
                  : "This feature is active in selected countries",
              }}
            >
              <Select
                mode="multiple"
                label={
                  flag.enabledGlobally ? "Enabled globally, the country selection is ignored" : "Enabled for the following countries (overrides global setting)"
                }
                value={pendingCountryUpdate?.flag.id === flag.id ? pendingCountryUpdate.enabledCountryCodes : getEnabledCountryCodes(flag)}
                onChange={(values) => {
                  const codes = values as SupportedMarket[];
                  const currentCodes = pendingCountryUpdate?.flag.id === flag.id ? pendingCountryUpdate.enabledCountryCodes : getEnabledCountryCodes(flag);
                  const isOnlyAddingCountries = codes.length > currentCodes.length && currentCodes.every((c) => codes.includes(c));
                  if (isOnlyAddingCountries) {
                    setPendingCountryUpdate({ flag, enabledCountryCodes: codes });
                  } else {
                    handleUpdateCountries(flag, codes);
                  }
                }}
                disabled={isPending || flag.enabledGlobally}
                showSearch
                style={{ minWidth: "400px" }}
              >
                {COUNTRY_OPTIONS.map(({ code }) => (
                  <Option value={code} key={code}>
                    {code}
                  </Option>
                ))}
              </Select>
            </TooltipWrapper>
          </Flex>
        ))
      ) : (
        <Typography variant="paragraph-m" color="grey-600">
          No feature flags available
        </Typography>
      )}
      <Modal
        cancelText="Cancel"
        content="This will make the feature available for all farms in all countries. Individual farm, country- and program-level settings will not be able to turn it off."
        onCancel={() => setPendingGlobalFlag(null)}
        onOk={onConfirmGlobal}
        okText="Enable"
        showModal={!!pendingGlobalFlag}
        title={`Enable ${pendingGlobalFlag ? getFlagDisplayName(pendingGlobalFlag) : "feature flag"} globally?`}
        loading={isPending}
      />
      <Modal
        cancelText="Cancel"
        content={`This will make the feature available for all farms in ${
          pendingCountryUpdate ? pendingCountryUpdate.enabledCountryCodes?.join(", ") : "selected countries"
        }. Global settings, individual farm settings or program-level settings will not be able to turn it off.`}
        onCancel={() => setPendingCountryUpdate(null)}
        onOk={onConfirmCountry}
        okText="Enable"
        showModal={!!pendingCountryUpdate}
        title={`Enable ${pendingCountryUpdate ? getFlagDisplayName(pendingCountryUpdate.flag) : "feature flag"} for selected countries?`}
        loading={isPending}
      />
    </MainPage>
  );
};
